// Fachsprache, Aufrufe und Entwurf der Gespraechsnotiz (ST-ACT-001, erster
// Schnitt; ADR-0051).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// identifiers.ts und aus demselben Grund.
//
// DIE WOERTER "INTERACTION", "TASK", "RETENTION" KOMMEN AUF DEM BILDSCHIRM
// NICHT VOR (NFR-UX-003, CM-UX-010; Story-UX 5.2.13). Auf dem Bildschirm heisst
// es "Notiz", "Verlauf", "festhalten". Eine Probe (interactions.test.ts) haelt
// jeden Text dieser Datei gegen eine Verbotsliste.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Die Grenze des Textes -- dieselbe Zahl wie MAXIMUM_NOTE_SUMMARY_LENGTH in
 * apps/api/src/domain/engagement/interaction-commands.ts. Die Probe liest die
 * Zahl aus dem API-Quelltext und schlaegt an, wenn beide auseinanderlaufen.
 * Die Oberflaeche ist die freundlichere Kontrolle, nie die einzige.
 */
export const MAXIMUM_NOTE_LENGTH = 2000;

/** Fachbegriffe der Interaktionstypen -- dieser Schnitt zeigt genau einen. */
export const INTERACTION_TYPE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  note: 'Notiz',
  call: 'Anruf',
  email: 'E-Mail',
  appointment: 'Termin',
  campaign: 'Kampagne',
  mailing: 'Versand',
  business_event: 'Fachereignis',
});

/** Rueckfall: der Rohwert sichtbar unuebersetzt, statt ein erfundenes Wort. */
export function interactionTypeLabel(code: string): string {
  return INTERACTION_TYPE_LABELS[code] ?? code;
}

/** Die Herkunft in Worten (Story-UX 5.2.5, Source-aware). */
export const SOURCE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  liaison_ui: 'von Hand festgehalten',
  outlook: 'aus Outlook',
});

export function sourceLabel(code: string): string {
  return SOURCE_LABELS[code] ?? code;
}

/**
 * Der Autor in Worten. Der Server nennt nur, ob die Notiz von der lesenden
 * Person stammt -- eine fremde Kennung waere kein Name, und ein Name braucht
 * ein Verzeichnis (ADR-0051, offener Punkt).
 */
export function authorLabel(authoredByMe: boolean, interactionType?: string): string {
  // Bei einer E-Mail ist die Autorin die Person, die sie ZUGEORDNET hat, nicht
  // die Absenderin; "E-Mail von dir" behauptete sonst, sie habe sie geschrieben.
  const wer = authoredByMe ? 'von dir' : 'von einer Kollegin oder einem Kollegen';
  return interactionType === 'email' ? `${wer} zugeordnet` : wer;
}

/**
 * Die eigene offene Wiedervorlage an einer Notiz (ST-ACT-001, zweiter Schnitt).
 * Hoechstens eine -- das haelt die Datenbank, nicht die Oberflaeche.
 */
export interface OwnFollowUpView {
  readonly taskId: string;
  readonly title: string;
  readonly dueOn: string;
  readonly overdue: boolean;
  readonly lockVersion: number;
}

export interface InteractionView {
  readonly interactionId: string;
  readonly interactionType: string;
  readonly occurredAt: string;
  readonly summary: string;
  readonly sourceSystem: string;
  readonly confidentialityClass: string;
  readonly createdAt: string;
  readonly authoredByMe: boolean;
  /** Fehlt, wenn es keine eigene offene gibt -- kein `null`, das die Oberflaeche deuten muesste. */
  readonly myOpenFollowUp?: OwnFollowUpView;
  /** Offene Wiedervorlagen aller im eigenen Kontext -- eine Zahl, keine Namen. */
  readonly openFollowUpCount: number;
}

export type InteractionsResult =
  | {
      readonly outcome: 'loaded';
      readonly interactions: readonly InteractionView[];
      readonly hasMore: boolean;
      readonly nextCursor?: string;
    }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type CreateNoteResult =
  | { readonly outcome: 'created'; readonly interactionId?: string }
  | { readonly outcome: 'invalid'; readonly errors: readonly { readonly fieldPath: string; readonly reasonCode: string }[] }
  | { readonly outcome: 'not-found' }
  | {
      readonly outcome: 'rejected';
      readonly message: PlainMessage;
      readonly kind: 'conflict' | 'other';
      /** Der Grundcode des Servers -- die Oberflaeche unterscheidet daran, ob die Notiz schon gespeichert IST. */
      readonly reasonCode?: string;
    };

/** Problem, Wirkung, naechster Schritt (NFR-UX-005) -- und keine Wiederholung, wo sie nichts aendert. */
export const CONTEXT_AMBIGUOUS_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Notiz konnte nicht festgehalten werden.',
  explanation:
    'Dein Konto arbeitet für mehrere Verlage zugleich. LIAISON muss wissen, für welchen die Notiz gilt, und kann das hier noch nicht auswählen.',
  nextStep: 'Wende dich an die LIAISON-Administration. Dein Text bleibt hier stehen.',
  retryable: false,
});

export const PARTY_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Zu diesem Kontakt kann keine Notiz mehr festgehalten werden.',
  explanation: 'Der Kontakt ist zusammengeführt, archiviert oder anonymisiert. Sein Verlauf ist abgeschlossen.',
  nextStep: 'Öffne den aktuellen Kontakt über die Kontaktliste.',
  retryable: false,
});

export const DUPLICATE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Notiz ist bereits festgehalten.',
  explanation: 'Der Eintrag wurde beim ersten Versuch gespeichert; ein zweiter wird nicht angelegt.',
  nextStep: 'Der Verlauf unten zeigt den gespeicherten Stand.',
  retryable: false,
  // Die Notiz steht schon da: ein Hinweis, kein Fehler.
  notice: true,
});

/** Der Satz fuer das Feld -- Problem und Loesung in einem Satz (NFR-UX-005). */
export function noteFieldProblem(raw: string): string | undefined {
  const text = raw.trim();
  if (text.length === 0) {
    return 'Schreib zuerst, was du festhalten möchtest.';
  }
  if ([...text].length > MAXIMUM_NOTE_LENGTH) {
    return 'Kürze die Notiz auf höchstens zweitausend Zeichen.';
  }
  return undefined;
}

export function validationMessageFor(fieldPath: string, reasonCode: string): string {
  if (fieldPath === 'summary') {
    if (reasonCode === 'field_too_long') return 'Kürze die Notiz auf höchstens zweitausend Zeichen.';
    if (reasonCode === 'field_malformed') {
      return 'Die Notiz enthält Zeichen, die LIAISON nicht speichern kann. Entferne sie und versuche es erneut.';
    }
    return 'Schreib zuerst, was du festhalten möchtest.';
  }
  return 'Diese Angabe wurde nicht angenommen. Lade den Kontakt neu und versuche es erneut.';
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

function viewOf(value: unknown): InteractionView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.interactionId !== 'string' ||
    typeof row.interactionType !== 'string' ||
    typeof row.occurredAt !== 'string' ||
    typeof row.summary !== 'string' ||
    typeof row.sourceSystem !== 'string' ||
    typeof row.confidentialityClass !== 'string' ||
    typeof row.createdAt !== 'string' ||
    typeof row.authoredByMe !== 'boolean' ||
    typeof row.openFollowUpCount !== 'number'
  ) {
    return undefined;
  }
  const followUp = followUpOf(row.myOpenFollowUp);
  if (row.myOpenFollowUp !== undefined && followUp === undefined) {
    // Eine halbe Wiedervorlage ist keine: lieber die ganze Seite als
    // unvollstaendig melden, als eine Zeile ohne Termin anzuzeigen.
    return undefined;
  }
  return {
    interactionId: row.interactionId,
    interactionType: row.interactionType,
    occurredAt: row.occurredAt,
    summary: row.summary,
    sourceSystem: row.sourceSystem,
    confidentialityClass: row.confidentialityClass,
    createdAt: row.createdAt,
    authoredByMe: row.authoredByMe,
    openFollowUpCount: row.openFollowUpCount,
    ...(followUp === undefined ? {} : { myOpenFollowUp: followUp }),
  };
}

function followUpOf(value: unknown): OwnFollowUpView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.taskId !== 'string' ||
    typeof row.title !== 'string' ||
    typeof row.dueOn !== 'string' ||
    typeof row.overdue !== 'boolean' ||
    typeof row.lockVersion !== 'number'
  ) {
    return undefined;
  }
  return { taskId: row.taskId, title: row.title, dueOn: row.dueOn, overdue: row.overdue, lockVersion: row.lockVersion };
}

export interface ReadOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly cursor?: string;
  readonly send?: SendLike;
}

export async function fetchInteractions(options: ReadOptions): Promise<InteractionsResult> {
  const send = options.send ?? fetch;
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/interactions${query}`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(body.interactions) ? body.interactions : [];
  const interactions: InteractionView[] = [];
  for (const row of rows) {
    const view = viewOf(row);
    if (view === undefined) {
      // Eine Zeile, die den Vertrag bricht, ist ein Fehler dieses Systems --
      // dieselbe Haltung wie in parties.ts.
      return {
        outcome: 'rejected',
        message: {
          headline: 'Der Verlauf lässt sich nicht anzeigen.',
          explanation: 'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
          nextStep: 'Lade den Kontakt neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
          retryable: true,
        },
      };
    }
    interactions.push(view);
  }
  return {
    outcome: 'loaded',
    interactions,
    hasMore: body.hasMore === true,
    ...(typeof body.nextCursor === 'string' ? { nextCursor: body.nextCursor } : {}),
  };
}

export interface CreateNoteOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly summary: string;
  /** Je Absendevorgang neu erzeugt; eine Wiederholung derselben Sendung traegt dieselbe Kennung. */
  readonly clientRequestId: string;
  readonly send?: SendLike;
}

/** Haelt die Notiz fest. Der Text reist im Rumpf, nie in der Adresse; die Antwort traegt ihn nicht zurueck. */
export async function createNote(options: CreateNoteOptions): Promise<CreateNoteResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/interactions`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ summary: options.summary, clientRequestId: options.clientRequestId }),
  });
  const payload = await response.json().catch(() => undefined);
  const record = (payload ?? {}) as Record<string, unknown>;
  if (response.status === 201) {
    // Die Kennung der neuen Notiz braucht der Verlauf fuer die Wiedervorlage in
    // einem Zug; fehlt sie, gibt es keine leere Kennung, sondern keine.
    return {
      outcome: 'created',
      ...(typeof record.interactionId === 'string' && record.interactionId.length > 0 ? { interactionId: record.interactionId } : {}),
    };
  }
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (response.status === 409) {
    const code = record.reasonCode;
    const message =
      code === 'interaction_context_ambiguous'
        ? CONTEXT_AMBIGUOUS_MESSAGE
        : code === 'party_not_writable'
          ? PARTY_NOT_WRITABLE_MESSAGE
          : code === 'interaction_duplicate'
            ? DUPLICATE_MESSAGE
            : plainMessageFor(typeof code === 'string' ? code : undefined, factsOf(response, payload));
    return { outcome: 'rejected', message, kind: 'conflict', ...(typeof code === 'string' ? { reasonCode: code } : {}) };
  }
  if (response.status === 422 && Array.isArray(record.errors)) {
    return {
      outcome: 'invalid',
      errors: record.errors.flatMap((row: unknown) => {
        const entry = (row ?? {}) as Record<string, unknown>;
        return typeof entry.fieldPath === 'string' && typeof entry.reasonCode === 'string'
          ? [{ fieldPath: entry.fieldPath, reasonCode: entry.reasonCode }]
          : [];
      }),
    };
  }
  return {
    outcome: 'rejected',
    message: plainMessageFor(typeof record.reasonCode === 'string' ? record.reasonCode : undefined, factsOf(response, payload)),
    kind: 'other',
  };
}

// ------------------------------------------------------------- Der Entwurf

/**
 * Eine angefangene Notiz ueberlebt Neuladen, Bildschirmwechsel und
 * Browserabsturz (CM-UX-004, NFR-UX-005; Story-Akzeptanz 1). Der Modulzustand
 * des Suchbegriffs reicht dafuer AUSDRUECKLICH NICHT: er stirbt mit dem
 * Neuladen.
 *
 * DIE IDENTITAETSGRENZE aus ADR-0049 Ziffer 2 gilt hier als BEDINGUNG:
 *   * Der Schluessel traegt Kontokennung UND Party; ein Entwurf wird nie fuer
 *     ein anderes Konto gelesen.
 *   * OHNE Kontokennung wird weder gelesen noch geschrieben (fail-closed) --
 *     ein Schluessel "undefined" waere allen Identitaeten gemeinsam
 *     (Security-Review 15.09.2026, F-03).
 *   * Jeder Entwurf traegt seinen Zeitpunkt und verfaellt nach DRAFT_TTL: ein
 *     ohne Abmeldung geschlossener Tab laesst keinen vertraulichen Text
 *     unbegrenzt auf der Platte (F-03; adversarialer Review P2).
 *   * Die Abmeldung leert JEDEN Entwurf (forgetNoteDrafts in main.tsx); die
 *     Anmeldung raeumt Entwuerfe FREMDER Konten ab, ohne sie zu lesen.
 *   * Nach dem Speichern wird der Entwurf geleert.
 *
 * `localStorage` und nicht `sessionStorage`: die Story verlangt den Stand nach
 * dem Browserabbruch, und den ueberlebt nur der dauerhafte Speicher.
 */
export const DRAFT_KEY_PREFIX = 'liaison.notiz.';
export const DRAFT_TTL_MILLISECONDS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  readonly text: string;
  readonly savedAt: number;
  /**
   * Die Anfragekennung des laufenden Absendevorgangs -- MIT dem Entwurf
   * gespeichert (Codex-Erstreview 15.09.2026): bricht der Browser zwischen
   * Commit und Antwort ab, traegt der naechste Versuch dieselbe Kennung und
   * wird zum 409 statt zur zweiten, nicht loeschbaren Notiz.
   */
  readonly requestId?: string;
  readonly faelligkeit?: string;
  readonly andererTag?: string;
  readonly anlass?: string;
}

export interface NoteDraft {
  readonly text: string;
  readonly requestId?: string;
  /** Die gewaehlte Wiedervorlage (Option und, bei "anderer Tag", der Tag); fehlt bei aelteren Entwuerfen. */
  readonly faelligkeit?: string;
  readonly andererTag?: string;
  /** Der selbst geaenderte Anlass der Wiedervorlage; fehlt er, folgt der Anlass dem Notizanfang. */
  readonly anlass?: string;
}

export interface DraftStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

function draftKey(accountId: string, partyId: string): string {
  return `${DRAFT_KEY_PREFIX}${accountId}.${partyId}`;
}

function usableAccount(accountId: string | undefined): accountId is string {
  return typeof accountId === 'string' && accountId.trim().length > 0;
}

function storeOrUndefined(store: DraftStore | undefined): DraftStore | undefined {
  if (store !== undefined) return store;
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function recallNoteDraft(
  accountId: string | undefined,
  partyId: string,
  store?: DraftStore,
  now: number = Date.now(),
): NoteDraft | undefined {
  const s = storeOrUndefined(store);
  if (s === undefined || !usableAccount(accountId)) return undefined;
  try {
    const raw = s.getItem(draftKey(accountId, partyId));
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    if (typeof parsed.text !== 'string' || typeof parsed.savedAt !== 'number' || now - parsed.savedAt > DRAFT_TTL_MILLISECONDS) {
      s.removeItem(draftKey(accountId, partyId));
      return undefined;
    }
    if (parsed.text.length === 0) return undefined;
    return {
      text: parsed.text,
      ...(typeof parsed.requestId === 'string' ? { requestId: parsed.requestId } : {}),
      ...(typeof parsed.faelligkeit === 'string' ? { faelligkeit: parsed.faelligkeit } : {}),
      ...(typeof parsed.andererTag === 'string' ? { andererTag: parsed.andererTag } : {}),
      ...(typeof parsed.anlass === 'string' ? { anlass: parsed.anlass } : {}),
    };
  } catch {
    return undefined;
  }
}

export function rememberNoteDraft(
  accountId: string | undefined,
  partyId: string,
  text: string,
  store?: DraftStore,
  now: number = Date.now(),
  requestId?: string,
  wiedervorlage?: { readonly faelligkeit: string; readonly andererTag: string; readonly anlass?: string | undefined },
): void {
  const s = storeOrUndefined(store);
  if (s === undefined || !usableAccount(accountId)) return;
  try {
    if (text.trim().length === 0) {
      s.removeItem(draftKey(accountId, partyId));
      return;
    }
    const draft: StoredDraft = {
      text,
      savedAt: now,
      ...(requestId === undefined ? {} : { requestId }),
      // "keine" ist der Ausgangszustand und wird nicht gespeichert.
      ...(wiedervorlage === undefined || wiedervorlage.faelligkeit === 'keine' ? {} : { faelligkeit: wiedervorlage.faelligkeit }),
      // Der Tag nur, solange "anderer Tag" gewaehlt ist: ein abgewaehlter Tag soll nicht wiederkehren.
      ...(wiedervorlage === undefined || wiedervorlage.faelligkeit !== 'anderer' || wiedervorlage.andererTag === '' ? {} : { andererTag: wiedervorlage.andererTag }),
      // Der Anlass nur, wenn er selbst geaendert wurde und eine Wiedervorlage gewaehlt ist.
      ...(wiedervorlage?.anlass === undefined || wiedervorlage.faelligkeit === 'keine' ? {} : { anlass: wiedervorlage.anlass }),
    };
    s.setItem(draftKey(accountId, partyId), JSON.stringify(draft));
  } catch {
    // Ein voller oder gesperrter Speicher darf die Arbeit nicht anhalten.
  }
}

export function forgetNoteDraft(accountId: string | undefined, partyId: string, store?: DraftStore): void {
  const s = storeOrUndefined(store);
  if (s === undefined || !usableAccount(accountId)) return;
  try {
    s.removeItem(draftKey(accountId, partyId));
  } catch {
    // siehe oben
  }
}

function keysWithPrefix(s: DraftStore): string[] {
  const keys: string[] = [];
  for (let index = 0; index < s.length; index += 1) {
    const key = s.key(index);
    if (key !== null && key.startsWith(DRAFT_KEY_PREFIX)) keys.push(key);
  }
  return keys;
}

/** Bei der Abmeldung: nichts darf das naechste Konto im selben Browser wiederfinden. */
export function forgetNoteDrafts(store?: DraftStore): void {
  const s = storeOrUndefined(store);
  if (s === undefined) return;
  try {
    for (const key of keysWithPrefix(s)) s.removeItem(key);
  } catch {
    // siehe oben
  }
}

/** Bei der Anmeldung: Entwuerfe FREMDER Konten verschwinden, ohne gelesen zu werden. */
export function forgetForeignNoteDrafts(accountId: string | undefined, store?: DraftStore): void {
  const s = storeOrUndefined(store);
  if (s === undefined) return;
  const own = usableAccount(accountId) ? `${DRAFT_KEY_PREFIX}${accountId}.` : undefined;
  try {
    for (const key of keysWithPrefix(s)) {
      if (own === undefined || !key.startsWith(own)) s.removeItem(key);
    }
  } catch {
    // siehe oben
  }
}

/** Eine neue Anfragekennung je Absendevorgang -- der Doppellogging-Schutz. */
export function newClientRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Die Zusammenfassung des Verlaufs, in Worten.
 *
 * SIE STEHT NICHT MEHR IN DER STATUSREGION (17.09.2026, Befund der
 * ST-TAX-002-Session): sie ist ein DAUERZUSTAND, und eine Region mit
 * dauerhaftem Text macht jede Meldung in jeder anderen Region desselben
 * Bildschirms zwangslaeufig zur zweiten. Was in die Region gehoert, ist das
 * ERGEBNIS einer Handlung -- dafuer gibt es `noteSavedAnnouncement`.
 */
export function historySummary(count: number, hasMore: boolean): string {
  if (count === 0) return 'Für diesen Kontakt ist noch keine Notiz festgehalten.';
  const zahl = count === 1 ? '1 Notiz' : `${count} Notizen`;
  return hasMore ? `${zahl} im Verlauf, ältere sind vorhanden.` : `${zahl} im Verlauf.`;
}

/**
 * Die Bestaetigung nach dem Festhalten -- MIT der neuen Zahl.
 *
 * Die Nutzerin soll hoeren, was ihre Handlung bewirkt hat, nicht nur
 * "Gespeichert": die Zahl ist das Ergebnis und gehoert deshalb in denselben
 * Satz. Vorher stand sie als Dauerzustand daneben in derselben Region und
 * wurde bei jeder Ansage mitgelesen.
 */
export function noteSavedAnnouncement(count: number, hasMore: boolean): string {
  return `Notiz festgehalten. Jetzt ${historySummary(count, hasMore)}`;
}

// --- Filter des Verlaufs (Slice 5a) ------------------------------------------

/** Die Zeile ueber der Liste, solange gefiltert wird: ehrlich ueber GELADENE Eintraege. */
export function filteredHistorySummary(shown: number, loaded: number, hasMore: boolean): string {
  const eintraege = loaded === 1 ? 'Eintrag' : 'Einträgen';
  return `${shown} von ${loaded} geladenen ${eintraege}${hasMore ? ', ältere sind noch nicht geladen.' : '.'}`;
}

export type VerlaufFilterId = 'alles' | 'notizen' | 'wiedervorlage' | `art:${string}`;

export interface VerlaufFilterOption {
  readonly id: VerlaufFilterId;
  readonly label: string;
}

/** Eigene offene Wiedervorlage oder eine offene irgendeiner Person im eigenen Haus. */
export function hatWiedervorlage(eintrag: Pick<InteractionView, 'myOpenFollowUp' | 'openFollowUpCount'>): boolean {
  return eintrag.myOpenFollowUp !== undefined || eintrag.openFollowUpCount > 0;
}

/**
 * Die Knoepfe der Filtergruppe: Alles, Notizen, Mit Wiedervorlage, dazu je
 * weitere Art, die ein GELADENER Eintrag hat -- kein toter Filter. Ohne
 * Eintraege gibt es keine Gruppe.
 */
export function filterOptionen(eintraege: readonly Pick<InteractionView, 'interactionType'>[]): readonly VerlaufFilterOption[] {
  if (eintraege.length === 0) {
    return [];
  }
  const weitere = [...new Set(eintraege.map((eintrag) => eintrag.interactionType))].filter((art) => art !== 'note');
  return [
    { id: 'alles', label: 'Alles' },
    { id: 'notizen', label: 'Notizen' },
    { id: 'wiedervorlage', label: 'Mit Wiedervorlage' },
    ...weitere.map((art): VerlaufFilterOption => ({ id: `art:${art}`, label: interactionTypeLabel(art) })),
  ];
}

export function filtern<T extends Pick<InteractionView, 'interactionType' | 'myOpenFollowUp' | 'openFollowUpCount'>>(
  eintraege: readonly T[],
  filter: VerlaufFilterId,
): readonly T[] {
  if (filter === 'alles') return eintraege;
  if (filter === 'notizen') return eintraege.filter((eintrag) => eintrag.interactionType === 'note');
  if (filter === 'wiedervorlage') return eintraege.filter(hatWiedervorlage);
  return eintraege.filter((eintrag) => `art:${eintrag.interactionType}` === filter);
}

/** Der Satz bei einem Filter ohne Treffer unter den geladenen Eintraegen. */
export function emptyFilterSentence(filter: VerlaufFilterId): string {
  return filter === 'wiedervorlage'
    ? 'Unter den geladenen Einträgen ist keiner mit Wiedervorlage.'
    : 'Unter den geladenen Einträgen ist keiner dieser Art.';
}
